import React, { useMemo, useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import api from '../../Utils/Axios';

function AmdaniBook({ companyLanguage = 'ur', company = null }) {
  const isUrdu = companyLanguage === 'ur';
  const [bills, setBills] = useState([]);
  const [showBillDetails, setShowBillDetails] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [savingRowId, setSavingRowId] = useState(null);
  const [filterDate, setFilterDate] = useState('');

  const [rows, setRows] = useState([]);

  const getToken = () => localStorage.getItem('companyToken');

  const fetchBills = async () => {
    try {
      const token = getToken();
      if (!token) return;

      // Try my-bills endpoint first (same as other pages)
      let billsData = [];
      try {
        const response = await api.get('/bill/company/my-bills', {
          headers: { Authorization: `Bearer ${token}` }
        });
        billsData = response.data.bills || [];
      } catch (error1) {
        // Fallback to /all endpoint if my-bills fails
        console.warn('my-bills endpoint failed, trying /all:', error1);
        try {
          const response = await api.get('/bill/company/all', {
            headers: { Authorization: `Bearer ${token}` }
          });
          billsData = response.data.bills || [];
        } catch (error2) {
          console.error('Both endpoints failed:', error2);
          throw error2;
        }
      }

      console.log('Fetched bills:', billsData.length);
      setBills(billsData);
    } catch (error) {
      console.error('Fetch bills error:', error);
      // Don't show error to user, just log it
    }
  };

  // Calculate Laga, Commission, Mashiana, and Market Fee from bills for a specific date
  const getBillTotalsForDate = (date) => {
    if (!date || !bills.length) return { laga: 0, commission: 0, mashiana: 0, marketFee: 0 };

    const dateStr = date.split('T')[0]; // Get YYYY-MM-DD format
    const billsForDate = bills.filter(bill => {
      if (!bill.date) return false;
      const billDate = new Date(bill.date).toLocaleDateString('en-CA');
      return billDate === dateStr;
    });

    let totalLaga = 0;
    let totalCommission = 0;
    let totalMashiana = 0;
    let totalMarketFee = 0;

    billsForDate.forEach(bill => {
      let expensesObj = bill.expenses || {};

      // Convert Map to object if needed
      if (expensesObj instanceof Map) {
        expensesObj = Object.fromEntries(expensesObj);
      } else if (expensesObj && typeof expensesObj === 'object' && expensesObj.forEach && typeof expensesObj.forEach === 'function') {
        const plainObj = {};
        expensesObj.forEach((val, key) => {
          plainObj[key] = val;
        });
        expensesObj = plainObj;
      }

      // Calculate Laga (لاگا)
      const lagaValue = parseFloat(
        expensesObj['لاگا'] ||
        expensesObj['Laga'] ||
        expensesObj.Laga ||
        0
      );
      totalLaga += lagaValue || 0;

      // Calculate Commission (کمیشن)
      const commissionValue = parseFloat(
        expensesObj['کمیشن'] ||
        expensesObj['Commission'] ||
        expensesObj.Commission ||
        0
      );
      totalCommission += commissionValue || 0;

      // Calculate Mashiana (مشیانه)
      const mashianaValue = parseFloat(
        expensesObj['مشیانه'] ||
        expensesObj['مشیانہ'] ||
        expensesObj['Miscellaneous'] ||
        expensesObj.Miscellaneous ||
        expensesObj['Mashiyana'] ||
        expensesObj.Mashiyana ||
        0
      );
      totalMashiana += mashianaValue || 0;

      // Calculate Market Fee (مارکیٹ فیس)
      const marketFeeValue = parseFloat(
        expensesObj['مارکیٹ فیس'] ||
        expensesObj['Market Fee'] ||
        expensesObj['MarketFee'] ||
        expensesObj.MarketFee ||
        0
      );
      totalMarketFee += marketFeeValue || 0;
    });

    console.log(`Date: ${date}, Bills found: ${billsForDate.length}, Laga: ${totalLaga}, Commission: ${totalCommission}, Mashiana: ${totalMashiana}, Market Fee: ${totalMarketFee}`);
    return { laga: totalLaga, commission: totalCommission, mashiana: totalMashiana, marketFee: totalMarketFee };
  };

  useEffect(() => {
    fetchBills();
  }, []);

  // Load saved data after bills are fetched
  useEffect(() => {
    if (bills.length > 0 || company?._id) {
      loadSavedData();
    }
  }, [bills, company?._id]);

  // Recalculate values when bills are loaded or rows change
  // Same logic as AkhrajatBook - always update from bills
  useEffect(() => {
    if (bills.length > 0 && rows.length > 0) {
      setRows(prevRows => prevRows.map(row => {
        if (!row.date) return row;
        const totals = getBillTotalsForDate(row.date);
        console.log(`Recalculating for date ${row.date}:`, totals);
        // Always update from bills - same as AkhrajatBook
        return {
          ...row,
          laga: totals.laga > 0 ? totals.laga.toString() : (totals.laga === 0 ? (row.laga || '0') : (row.laga || '')),
          commission: totals.commission > 0 ? totals.commission.toString() : (totals.commission === 0 ? (row.commission || '0') : (row.commission || '')),
          mashiana: totals.mashiana > 0 ? totals.mashiana.toString() : (totals.mashiana === 0 ? (row.mashiana || '0') : (row.mashiana || '')),
          marketFee: totals.marketFee > 0 ? totals.marketFee.toString() : (totals.marketFee === 0 ? (row.marketFee || '0') : (row.marketFee || ''))
        };
      }));
    }
  }, [bills]);

  // Load saved data from localStorage and ensure all bill dates are included
  const loadSavedData = () => {
    if (!company?._id) return;
    try {
      const savedKey = `amdaniBookRows_${company._id}`;
      const savedData = localStorage.getItem(savedKey);
      let savedRows = [];

      if (savedData) {
        savedRows = JSON.parse(savedData);
      }

      // Get all unique dates from bills
      const billDates = new Set();
      bills.forEach(bill => {
        if (bill.date) {
          const billDate = new Date(bill.date).toLocaleDateString('en-CA');
          billDates.add(billDate);
        }
      });

      // Always ensure today's date has an entry
      const todayDate = new Date().toLocaleDateString('en-CA');
      billDates.add(todayDate);

      // Create a map of existing rows by date for quick lookup
      const existingRowsByDate = new Map();
      savedRows.forEach(row => {
        if (row.date) {
          const rowDate = row.date.split('T')[0];
          existingRowsByDate.set(rowDate, row);
        }
      });

      // Add rows for all bill dates that don't exist yet
      const allRows = [...savedRows];
      billDates.forEach(dateStr => {
        if (!existingRowsByDate.has(dateStr)) {
          allRows.push({
            id: Date.now() + Math.random() + dateStr,
            date: dateStr,
            laga: '',
            commission: '',
            mashiana: '',
            marketFee: ''
          });
        }
      });

      // Sort rows by date (newest first)
      allRows.sort((a, b) => {
        if (!a.date || !b.date) return 0;
        return new Date(b.date) - new Date(a.date);
      });

      // Save updated rows to localStorage
      localStorage.setItem(savedKey, JSON.stringify(allRows));

      // Set rows with all data (saved + bill dates)
      setRows(allRows);

      // After setting rows, update values from bills if bills are already loaded
      if (bills.length > 0) {
        setTimeout(() => {
          setRows(currentRows => currentRows.map(row => {
            if (!row.date) return row;
            const totals = getBillTotalsForDate(row.date);
            return {
              ...row,
              laga: totals.laga > 0 ? totals.laga.toString() : (totals.laga === 0 ? '0' : (row.laga || '')),
              commission: totals.commission > 0 ? totals.commission.toString() : (totals.commission === 0 ? '0' : (row.commission || '')),
              mashiana: totals.mashiana > 0 ? totals.mashiana.toString() : (totals.mashiana === 0 ? '0' : (row.mashiana || '')),
              marketFee: totals.marketFee > 0 ? totals.marketFee.toString() : (totals.marketFee === 0 ? '0' : (row.marketFee || ''))
            };
          }));
        }, 100);
      }
    } catch (error) {
      console.error('Error loading saved data:', error);
    }
  };

  // Save row data to localStorage
  const handleSaveRow = (rowId) => {
    if (!company?._id) return;
    setSavingRowId(rowId);
    try {
      const rowToSave = rows.find(r => r.id === rowId);
      if (rowToSave) {
        const savedKey = `amdaniBookRows_${company._id}`;
        const existingData = localStorage.getItem(savedKey);
        let allRows = [];
        if (existingData) {
          allRows = JSON.parse(existingData);
        }

        // Update or add the row
        const existingIndex = allRows.findIndex(r => r.id === rowId);
        if (existingIndex >= 0) {
          allRows[existingIndex] = rowToSave;
        } else {
          allRows.push(rowToSave);
        }

        localStorage.setItem(savedKey, JSON.stringify(allRows));
        // Also update current rows state
        setRows(allRows);
        toast.success(isUrdu ? 'قطار محفوظ ہو گئی' : 'Row saved successfully');
      }
    } catch (error) {
      console.error('Error saving row:', error);
      toast.error(isUrdu ? 'محفوظ کرنے میں ناکام' : 'Failed to save');
    } finally {
      setTimeout(() => setSavingRowId(null), 500);
    }
  };

  // Auto-fill fields when bills are loaded
  useEffect(() => {
    if (bills.length > 0 && rows.length > 0) {
      setRows(prevRows => prevRows.map(row => {
        if (!row.date) return row;
        const totals = getBillTotalsForDate(row.date);
        console.log(`Recalculating for date ${row.date}:`, totals);
        // Update values from bills - preserve manual entries only if bills have no data
        return {
          ...row,
          laga: totals.laga > 0 ? totals.laga.toString() : (totals.laga === 0 ? (row.laga || '0') : (row.laga || '')),
          commission: totals.commission > 0 ? totals.commission.toString() : (totals.commission === 0 ? (row.commission || '0') : (row.commission || '')),
          mashiana: totals.mashiana > 0 ? totals.mashiana.toString() : (totals.mashiana === 0 ? (row.mashiana || '0') : (row.mashiana || '')),
          marketFee: totals.marketFee > 0 ? totals.marketFee.toString() : (totals.marketFee === 0 ? (row.marketFee || '0') : (row.marketFee || ''))
        };
      }));
    }
  }, [bills]);

  const addRow = () => {
    const newDate = new Date().toLocaleDateString('en-CA');

    // Check if a row with this date already exists
    const dateExists = rows.some(row => {
      if (!row.date) return false;
      const rowDate = row.date.split('T')[0];
      return rowDate === newDate;
    });

    if (dateExists) {
      toast.warning(isUrdu ? 'اس تاریخ کی ایک قطار پہلے سے موجود ہے' : 'A row for this date already exists');
      return;
    }

    setRows(prev => [...prev, {
      id: Date.now() + Math.random(),
      date: newDate,
      laga: '',
      commission: '',
      mashiana: '',
      marketFee: ''
    }]);
  };

  const removeRow = (id) => {
    if (company?._id) {
      try {
        const savedKey = `amdaniBookRows_${company._id}`;
        const existingData = localStorage.getItem(savedKey);
        if (existingData) {
          let allRows = JSON.parse(existingData);
          allRows = allRows.filter(r => r.id !== id);
          localStorage.setItem(savedKey, JSON.stringify(allRows));
        }
      } catch (error) {
        console.error('Error removing row from localStorage:', error);
      }
    }
    setRows(prev => prev.length === 1 ? prev : prev.filter(r => r.id !== id));
  };

  const handleChange = (id, field, value) => {
    setRows(prev => prev.map(r => {
      if (r.id !== id) return r;
      const updatedRow = { ...r, [field]: field === 'date' ? value : value.replace(/[^0-9.]/g, '') };

      // Auto-fill from bills when date changes
      if (field === 'date' && value) {
        const totals = getBillTotalsForDate(value);
        // Always update from bills when date changes
        updatedRow.laga = totals.laga > 0 ? totals.laga.toString() : (totals.laga === 0 ? '0' : '');
        updatedRow.commission = totals.commission > 0 ? totals.commission.toString() : (totals.commission === 0 ? '0' : '');
        updatedRow.mashiana = totals.mashiana > 0 ? totals.mashiana.toString() : (totals.mashiana === 0 ? '0' : '');
        updatedRow.marketFee = totals.marketFee > 0 ? totals.marketFee.toString() : (totals.marketFee === 0 ? '0' : '');
      }

      return updatedRow;
    }));
  };

  // Show all rows - filter is optional and only for reference/viewing
  // But always show all saved rows in the table
  const filteredRows = useMemo(() => {
    // Always return all rows - filterDate is just for reference/viewing
    // User can see all dates in the table
    return rows;
  }, [rows]);

  const withTotals = useMemo(() => filteredRows.map(r => {
    const laga = parseFloat(r.laga) || 0;
    const commission = parseFloat(r.commission) || 0;
    const mashiana = parseFloat(r.mashiana) || 0;
    const marketFee = parseFloat(r.marketFee) || 0;
    return { ...r, total: laga + commission + mashiana + marketFee };
  }), [filteredRows]);

  const overallTotals = useMemo(() => {
    return withTotals.reduce((acc, r) => ({
      laga: acc.laga + (parseFloat(r.laga) || 0),
      commission: acc.commission + (parseFloat(r.commission) || 0),
      mashiana: acc.mashiana + (parseFloat(r.mashiana) || 0),
      marketFee: acc.marketFee + (parseFloat(r.marketFee) || 0),
      total: acc.total + (r.total || 0)
    }), { laga: 0, commission: 0, mashiana: 0, marketFee: 0, total: 0 });
  }, [withTotals]);

  const handleExportExcel = () => {
    const excelData = [
      [
        isUrdu ? 'تاریخ' : 'Date',
        isUrdu ? 'لاگا' : 'Laga',
        isUrdu ? 'کمیشن' : 'Commission',
        isUrdu ? 'مشیانه' : 'Mashiana',
        isUrdu ? 'مارکیٹ فیس' : 'Market Fee',
        isUrdu ? 'کل' : 'Total'
      ]
    ];

    withTotals.forEach(row => {
      excelData.push([
        row.date ? new Date(row.date).toLocaleDateString() : '',
        parseFloat(row.laga) || 0,
        parseFloat(row.commission) || 0,
        parseFloat(row.mashiana) || 0,
        parseFloat(row.marketFee) || 0,
        row.total || 0
      ]);
    });

    // Add totals row
    excelData.push([
      isUrdu ? 'کل' : 'Total',
      overallTotals.laga,
      overallTotals.commission,
      overallTotals.mashiana,
      overallTotals.marketFee,
      overallTotals.total
    ]);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    XLSX.utils.book_append_sheet(wb, ws, isUrdu ? 'آمدنی بُک' : 'Amdani Book');
    XLSX.writeFile(wb, 'amdani-book.xlsx');
  };

  const handlePrint = () => {
    window.print();
  };

  // Print styles
  const printStyles = `
    @media print {
      @page { margin: 0.6cm; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  `;

  return (
    <div className={`min-h-screen bg-brand-background p-6 print:p-0 print:bg-white ${isUrdu ? 'font-urdu' : ''}`} dir={isUrdu ? 'rtl' : 'ltr'}>
      <style>{printStyles}</style>
      <div className="max-w-6xl mx-auto">
        <div className="bg-white shadow-lg rounded-xl p-6 border border-brand-card mb-6 print:shadow-none print:border-none print:mb-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className={`text-2xl font-bold text-brand-header print:text-xl ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                {isUrdu ? 'آمدنی بُک' : 'Amdani Book'}
              </h1>
              <p className="text-gray-600 text-sm print:hidden">
                {isUrdu ? 'روزانہ آمدنی ریکارڈ کریں اور Excel میں ڈاؤن لوڈ کریں' : 'Track daily income and export to Excel'}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 print:hidden items-center">
              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                  {isUrdu ? 'تاریخ سے فلٹر:' : 'Filter by Date:'}
                </label>
                <input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 text-sm"
                />
                {filterDate && (
                  <button
                    onClick={() => setFilterDate('')}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-2 rounded text-sm transition-all"
                  >
                    {isUrdu ? 'کلیر' : 'Clear'}
                  </button>
                )}
              </div>
              <button
                onClick={() => setShowBillDetails(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all"
              >
                {isUrdu ? 'بل تفصیل' : 'Bill Details'}
              </button>
              <button
                onClick={addRow}
                className="bg-brand-header hover:bg-emerald-800 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all"
              >
                {isUrdu ? 'نیا اندراج' : 'Add Row'}
              </button>
              <button
                onClick={handleExportExcel}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all"
              >
                {isUrdu ? 'Excel ڈاؤن لوڈ' : 'Download Excel'}
              </button>
              <button
                onClick={handlePrint}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all"
              >
                {isUrdu ? 'پرنٹ' : 'Print'}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white shadow-lg rounded-xl p-4 border border-brand-card overflow-x-auto print:shadow-none print:border-none print:p-0">
          <table className="w-full min-w-[1000px]">
            <thead className="bg-gray-100 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-2 text-right font-semibold whitespace-nowrap">{isUrdu ? 'تاریخ' : 'Date'}</th>
                <th className="px-4 py-2 text-right font-semibold whitespace-nowrap">{isUrdu ? 'لاگا' : 'Laga'}</th>
                <th className="px-4 py-2 text-right font-semibold whitespace-nowrap">{isUrdu ? 'کمیشن' : 'Commission'}</th>
                <th className="px-4 py-2 text-right font-semibold whitespace-nowrap">{isUrdu ? 'مشیانه' : 'Mashiana'}</th>
                <th className="px-4 py-2 text-right font-semibold whitespace-nowrap">{isUrdu ? 'مارکیٹ فیس' : 'Market Fee'}</th>
                <th className="px-4 py-2 text-right font-semibold whitespace-nowrap">{isUrdu ? 'کل' : 'Total'}</th>
                <th className="px-4 py-2 text-center font-semibold print:hidden whitespace-nowrap">{isUrdu ? 'عمل' : 'Action'}</th>
              </tr>
            </thead>
            <tbody>
              {withTotals.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    {isUrdu ? 'کوئی ڈیٹا نہیں ملا' : 'No data found'}
                  </td>
                </tr>
              ) : (
                withTotals.map((row) => (
                  <tr key={row.id} className="border-b border-gray-100">
                    <td className="px-4 py-2">
                      <input
                        type="date"
                        value={row.date}
                        onChange={(e) => handleChange(row.id, 'date', e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={row.laga}
                        onChange={(e) => handleChange(row.id, 'laga', e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm text-right"
                        placeholder="0"
                        readOnly
                        title={isUrdu ? 'یہ فیلڈ بلز سے خود بخود بھر جائے گی' : 'This field will be auto-filled from bills'}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={row.commission}
                        onChange={(e) => handleChange(row.id, 'commission', e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm text-right"
                        placeholder="0"
                        readOnly
                        title={isUrdu ? 'یہ فیلڈ بلز سے خود بخود بھر جائے گی' : 'This field will be auto-filled from bills'}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={row.mashiana}
                        onChange={(e) => handleChange(row.id, 'mashiana', e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm text-right"
                        placeholder="0"
                        readOnly
                        title={isUrdu ? 'یہ فیلڈ بلز سے خود بخود بھر جائے گی' : 'This field will be auto-filled from bills'}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={row.marketFee}
                        onChange={(e) => handleChange(row.id, 'marketFee', e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm text-right"
                        placeholder="0"
                        readOnly
                        title={isUrdu ? 'یہ فیلڈ بلز سے خود بخود بھر جائے گی' : 'This field will be auto-filled from bills'}
                      />
                    </td>
                    <td className="px-4 py-2 text-right font-semibold text-brand-header">
                      Rs. {(row.total || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-center print:hidden">
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() => handleSaveRow(row.id)}
                          disabled={savingRowId === row.id}
                          className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm transition-all disabled:opacity-50"
                        >
                          {savingRowId === row.id ? (isUrdu ? 'محفوظ...' : 'Saving...') : (isUrdu ? 'محفوظ کریں' : 'Save')}
                        </button>
                        <button
                          onClick={() => {
                            setSelectedDate(row.date);
                            setShowBillDetails(true);
                          }}
                          className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm transition-all"
                        >
                          {isUrdu ? 'بل تفصیل' : 'Bill Details'}
                        </button>
                        <button
                          onClick={() => removeRow(row.id)}
                          className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-sm transition-all disabled:opacity-50"
                          disabled={rows.length === 1}
                        >
                          {isUrdu ? 'ختم کریں' : 'Remove'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td className="px-4 py-3 text-right font-bold">{isUrdu ? 'کل' : 'Total'}</td>
                <td className="px-4 py-3 text-right font-bold text-brand-header">Rs. {overallTotals.laga.toLocaleString()}</td>
                <td className="px-4 py-3 text-right font-bold text-brand-header">Rs. {overallTotals.commission.toLocaleString()}</td>
                <td className="px-4 py-3 text-right font-bold text-brand-header">Rs. {overallTotals.mashiana.toLocaleString()}</td>
                <td className="px-4 py-3 text-right font-bold text-brand-header">Rs. {overallTotals.marketFee.toLocaleString()}</td>
                <td className="px-4 py-3 text-right font-bold text-brand-header">Rs. {overallTotals.total.toLocaleString()}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>


      {/* Bill Details Modal */}
      {showBillDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-brand-header text-white p-4 flex justify-between items-center">
              <div>
                <h2 className={`text-xl font-bold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {isUrdu ? 'بل تفصیل' : 'Bill Details'}
                </h2>
                {selectedDate && (
                  <p className="text-sm text-gray-200 mt-1">
                    {isUrdu ? 'تاریخ:' : 'Date:'} {new Date(selectedDate).toLocaleDateString('en-GB')}
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowBillDetails(false);
                  setSelectedDate(null);
                }}
                className="text-white hover:text-gray-200 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-auto p-4">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[800px]">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-right font-semibold">{isUrdu ? 'بل نمبر' : 'Bill Number'}</th>
                      <th className="px-4 py-2 text-right font-semibold">{isUrdu ? 'تاریخ' : 'Date'}</th>
                      <th className="px-4 py-2 text-right font-semibold">{isUrdu ? 'سپلائر' : 'Supplier'}</th>
                      <th className="px-4 py-2 text-right font-semibold">{isUrdu ? 'لاگا' : 'Laga'}</th>
                      <th className="px-4 py-2 text-right font-semibold">{isUrdu ? 'کمیشن' : 'Commission'}</th>
                      <th className="px-4 py-2 text-right font-semibold">{isUrdu ? 'مشیانه' : 'Mashiana'}</th>
                      <th className="px-4 py-2 text-right font-semibold">{isUrdu ? 'مارکیٹ فیس' : 'Market Fee'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Filter bills by selected date if date is selected
                      let filteredBills = bills;
                      if (selectedDate) {
                        const dateStr = selectedDate.split('T')[0];
                        filteredBills = bills.filter(bill => {
                          if (!bill.date) return false;
                          const billDate = new Date(bill.date).toLocaleDateString('en-CA');
                          return billDate === dateStr;
                        });
                      }

                      if (filteredBills.length === 0) {
                        return (
                          <tr key="no-bills">
                            <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                              {isUrdu ? 'کوئی بل نہیں ملا' : 'No bills found'}
                            </td>
                          </tr>
                        );
                      }

                      return filteredBills.map((bill) => {
                        let expensesObj = bill.expenses || {};

                        // Convert Map to object if needed
                        if (expensesObj instanceof Map) {
                          expensesObj = Object.fromEntries(expensesObj);
                        } else if (expensesObj && typeof expensesObj === 'object' && expensesObj.forEach && typeof expensesObj.forEach === 'function') {
                          const plainObj = {};
                          expensesObj.forEach((val, key) => {
                            plainObj[key] = val;
                          });
                          expensesObj = plainObj;
                        }

                        const laga = parseFloat(expensesObj['لاگا'] || expensesObj['Laga'] || 0);
                        const commission = parseFloat(expensesObj['کمیشن'] || expensesObj['Commission'] || 0);
                        const mashiana = parseFloat(expensesObj['مشیانه'] || expensesObj['مشیانہ'] || expensesObj['Miscellaneous'] || 0);
                        const marketFee = parseFloat(expensesObj['مارکیٹ فیس'] || expensesObj['Market Fee'] || expensesObj['MarketFee'] || 0);

                        const billDate = bill.date ? new Date(bill.date).toLocaleDateString('en-GB') : '-';
                        const billNumber = bill.serialNumber || bill.billNumber || bill._id?.toString().slice(-6) || '-';
                        const supplierName = bill.customerNameUrdu || bill.customerName || '-';

                        return (
                          <tr key={bill._id} className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-4 py-2 text-right">{billNumber}</td>
                            <td className="px-4 py-2 text-right">{billDate}</td>
                            <td className="px-4 py-2 text-right" style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {supplierName}
                            </td>
                            <td className="px-4 py-2 text-right">Rs. {laga.toLocaleString()}</td>
                            <td className="px-4 py-2 text-right">Rs. {commission.toLocaleString()}</td>
                            <td className="px-4 py-2 text-right">Rs. {mashiana.toLocaleString()}</td>
                            <td className="px-4 py-2 text-right">Rs. {marketFee.toLocaleString()}</td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 p-4 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowBillDetails(false);
                  setSelectedDate(null);
                }}
                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg text-sm font-semibold transition-all"
              >
                {isUrdu ? 'بند کریں' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AmdaniBook;

